<script setup lang="ts">
// TODO:
// - Remove the carousel controls? We just want to show the logos in a row and/or flex/grid.
// - Add alt text and optional url to the logos

import { ContentFileImageVariant, tryImageVariant } from '#imports'

const { logos = [], grayscale = false, defaultMaxWidth = undefined } = defineProps<{
  logos?: {
    image: ContentFile
    altText?: string
    url?: string | null
    maxWidth?: string
  }[]
  grayscale?: boolean
  defaultMaxWidth?: number
}>()

// Carousel controls
const carousel = ref<HTMLElement | null>(null)
const showLeftButton = ref(false)
const showRightButton = ref(true)

const threshold = 10

const scroll = (direction: 'left' | 'right') => {
  if (!carousel.value) return

  const scrollAmount = direction === 'left' ? -300 : 300
  const currentScroll = carousel.value.scrollLeft
  const newScrollLeft = Math.max(0, currentScroll + scrollAmount)

  carousel.value.scrollTo({
    left: newScrollLeft,
    behavior: 'smooth',
  })

  // Check scroll position after animation completes
  setTimeout(toggleButtons, 300)
}

const toggleButtons = () => {
  if (!carousel.value) return

  const maxScrollLeft = carousel.value.scrollWidth - carousel.value.clientWidth
  showRightButton.value = carousel.value.scrollLeft < maxScrollLeft - threshold
  showLeftButton.value = carousel.value.scrollLeft > threshold
}

// Debounce the scroll event
const debouncedToggleButtons = useDebounceFn(toggleButtons, 100)

onMounted(() => {
  if (carousel.value) {
    carousel.value.addEventListener('scroll', debouncedToggleButtons)
    toggleButtons()
  }
})

onBeforeUnmount(() => {
  if (carousel.value) {
    carousel.value.removeEventListener('scroll', debouncedToggleButtons)
  }
})
</script>

<template>
  <ContentBlockViewPartialContainer
    class="relative py-8"
    background-color="transparent"
  >
    
    <div
      ref="carousel"
      class="hidden-scrollbar flex flex-wrap items-center justify-center gap-12 overflow-x-auto scroll-smooth lg:justify-between"
    >
      <div
        v-for="(logo, index) in logos"
        :key="index"
        class="flex-none"
      >
        <component
          :is="logo.url ? 'a' : 'div'"
          v-bind="logo.url ? { href: logo.url, target: '_blank', rel: 'noopener' } : {}"
        >
          <img
            :src="tryImageVariant(logo.image, ContentFileImageVariant.Small)"
            :alt="logo.altText || `Logo ${index + 1}`"
            :class="{
              'grayscale': grayscale,
            }"
            :style="{
              maxWidth: logo.maxWidth || defaultMaxWidth ? `${logo.maxWidth || defaultMaxWidth}px` : undefined,
            }"
          >
        </component>
      </div>
    </div>

    
    <div
      v-if="showLeftButton"
      class="absolute left-0 top-1/2 -translate-y-1/2"
    >
      <BaseButtonIcon
        color="primary"
        shape="curved"
        @click="() => scroll('left')"
      >
        <Icon name="ph:arrow-left" />
      </BaseButtonIcon>
    </div>

    <div
      v-if="showRightButton"
      class="absolute right-0 top-1/2 -translate-y-1/2"
    >
      <BaseButtonIcon
        color="primary"
        shape="curved"
        @click="() => scroll('right')"
      >
        <Icon name="ph:arrow-right" />
      </BaseButtonIcon>
    </div>
  </ContentBlockViewPartialContainer>
</template>

<style scoped>
.hidden-scrollbar {
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
}
</style>
